// The Photohawk Studio Meta pixel ("Photohawk Website" dataset), so Meta ads can optimise for real Studio sign-ups.
//
// Consent first: nothing loads or fires, and no Meta cookie value is read or sent, unless the visitor accepted
// analytics/advertising cookies in the banner (lib/consent.ts). Scope: production photohawk.com only — the marketing,
// sign-up and Studio admin pages (isStudioPixelPage). Never a gallery's buyers (lib/gallery/pixel.ts is the
// photographer's own pixel), the participant pages, the Photohawk app's pages (/personal…) or staging.
//
// Each funnel event goes from the browser AND from the server (Conversions API, photohawk-serverless
// lib/metaCapi.js) with the same event id, so Meta counts it once:
//   CompleteRegistration  reg-<base>   /register, back from Stripe Checkout (the account now exists)
//   WorkspaceCreated      ws-<base>    same moment: the sign-up's first workspace (custom event)
//   Subscribe             sub-<base>   same moment, for a paid plan (its monthly fee, USD)
//   Subscribe             sub-<uuid>   an upgrade to a paid plan in Billing (lib/api/account.ts changePlan)
//   FirstUpload           (server only: the first photo lands in the upload pipeline, not in a browser)
// <base> is made here at sign-up and sent with the consent (`marketing`) to POST /users/register.

import { getConsent, hasAnalyticsConsent } from "@/lib/consent";
import { photohawkApi } from "@/lib/photohawkApi";
import { getPhotohawkEnv } from "@/lib/env";
import { ensureFbq, type Fbq } from "@/lib/metaPixel";
import { planCatalog } from "@/lib/plans/entitlements";

export const STUDIO_PIXEL_ID = "308014882006091";
const CURRENCY = "USD";
const PENDING_KEY = "ph_signup_meta";
const FBCLID_KEY = "ph_fbclid";
const PENDING_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

// Marketing / sign-up pages on photohawk.com (exact paths, then prefixes). Participant pages (/find-your-photos,
// /for-participants, /whatsapp, /reel…), the Photohawk app's pages (/personal…), account/payee pages and staff
// /platform pages are not listed, so they never load the pixel.
const APEX_PATHS = new Set([
  "/", "/studio", "/download", "/about", "/login", "/register", "/pricing", "/pricing-changes-2026",
  "/for-photographers", "/for-organisers", "/preview-protection", "/preview-first-uploads", "/web-uploader",
  "/on-demand-ftp", "/live-uploading", "/face-data-changes-2026",
]);
const APEX_PREFIXES = ["/admin/", "/guides", "/help", "/blog", "/event-photography"];
const PROD_APEX = new Set(["photohawk.com", "www.photohawk.com"]);
// Not workspace aliases (lib/hostTenant RESERVED_SUBDOMAINS' public ones): never Studio admin.
const NOT_ALIAS = new Set(["www", "upload", "api", "admin", "auth", "search", "checkout", "cdn", "assets", "static", "media"]);

/** Whether the Studio pixel may run on this page (pure; consent is checked separately). */
export const isStudioPixelPage = (hostname: string, pathname: string, env: string): boolean => {
  if (env !== "prod") return false;
  const host = hostname.toLowerCase().replace(/\.$/, "");
  const path = (pathname.replace(/\/+$/, "") || "/").toLowerCase();
  if (PROD_APEX.has(host)) {
    return APEX_PATHS.has(path) || APEX_PREFIXES.some((p) => (p.endsWith("/") ? path.startsWith(p) : path === p || path.startsWith(`${p}/`)));
  }
  // A workspace's own admin on {alias}.photohawk.com (the Studio customer, never their buyers' pages).
  if (host.endsWith(".photohawk.com")) {
    const label = host.slice(0, -".photohawk.com".length);
    if (!label || label.includes(".") || NOT_ALIAS.has(label)) return false;
    return path === "/admin" || path.startsWith("/admin/");
  }
  return false;
};

const here = (): { host: string; path: string } | null =>
  typeof window === "undefined" ? null : { host: window.location.hostname, path: window.location.pathname };

const currentEnv = (): string => {
  try {
    return getPhotohawkEnv();
  } catch {
    return "staging";
  }
};

let inited = false;
/** The pixel, initialised once — or null without consent or outside the Studio pages. */
const studioFbq = (): Fbq | null => {
  if (!hasAnalyticsConsent()) return null;
  const loc = here();
  if (!loc || !isStudioPixelPage(loc.host, loc.path, currentEnv())) return null;
  const fbq = ensureFbq();
  if (!fbq) return null;
  if (!inited) {
    fbq("init", STUDIO_PIXEL_ID);
    inited = true;
  }
  return fbq;
};

const STANDARD = new Set(["PageView", "CompleteRegistration", "Subscribe", "StartTrial", "Purchase", "Lead"]);

/**
 * Fire one event to the Studio pixel only (trackSingle: never to another pixel on the page). `eventId` is the id the
 * server's Conversions API event carries too.
 */
export const trackStudioEvent = (name: string, data?: Record<string, unknown>, eventId?: string): void => {
  const fbq = studioFbq();
  if (!fbq) return;
  try {
    fbq(STANDARD.has(name) ? "trackSingle" : "trackSingleCustom", STUDIO_PIXEL_ID, name, data || {}, eventId ? { eventID: eventId } : undefined);
  } catch {
    /* best-effort */
  }
};

/** A PageView for the current page (StudioPixel calls it on every route change once consent is given). */
export const trackStudioPageView = (): void => {
  rememberFbclid();
  trackStudioEvent("PageView");
};

// ---------------------------------------------------------------------------------------------- Meta cookie values

const readCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined;
  try {
    const hit = document.cookie.split(";").map((c) => c.trim()).find((c) => c.startsWith(`${name}=`));
    return hit ? decodeURIComponent(hit.slice(name.length + 1)) : undefined;
  } catch {
    return undefined;
  }
};

const FBCLID_RE = /^[A-Za-z0-9_-]{1,500}$/;

/** Keep an ad click's fbclid for this visit (consent only), so a sign-up a few pages later is still attributed. */
export const rememberFbclid = (): void => {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return;
  try {
    const id = new URLSearchParams(window.location.search).get("fbclid");
    if (id && FBCLID_RE.test(id)) window.sessionStorage.setItem(FBCLID_KEY, JSON.stringify({ id, ts: Date.now() }));
  } catch {
    /* storage blocked */
  }
};

/** _fbc, or one built from the visit's fbclid (Meta's format fb.1.<ms>.<fbclid>). */
export const readFbc = (): string | undefined => {
  const cookie = readCookie("_fbc");
  if (cookie) return cookie;
  if (typeof window === "undefined") return undefined;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("fbclid");
    if (fromUrl && FBCLID_RE.test(fromUrl)) return `fb.1.${Date.now()}.${fromUrl}`;
    const saved = JSON.parse(window.sessionStorage.getItem(FBCLID_KEY) || "null") as { id?: string; ts?: number } | null;
    if (saved && typeof saved.id === "string" && FBCLID_RE.test(saved.id)) return `fb.1.${saved.ts || Date.now()}.${saved.id}`;
  } catch {
    /* ignore */
  }
  return undefined;
};

const newId = (): string => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`;
};

export interface MarketingConsentPayload {
  consent: boolean;
  fbp?: string;
  fbc?: string;
  eventBase?: string;
  eventId?: string;
}

/**
 * What a request carries to the server about Meta: `{ consent: false }` without consent (nothing else), else the
 * consent plus the Meta cookie values. The server sends nothing to Meta unless `consent === true`.
 */
export const marketingPayload = (extra: { eventBase?: string; eventId?: string } = {}): MarketingConsentPayload => {
  if (!hasAnalyticsConsent()) return { consent: false };
  const out: MarketingConsentPayload = { consent: true, ...extra };
  const fbp = readCookie("_fbp");
  if (fbp) out.fbp = fbp;
  const fbc = readFbc();
  if (fbc) out.fbc = fbc;
  return out;
};

/** A plan's monthly fee in USD from the plan catalog in force (0 for an unknown or free plan). */
export const planMonthlyFee = (plan: string | null | undefined): number => {
  const id = String(plan ?? "").trim().toLowerCase();
  if (!id) return 0;
  const ids = id === "payasyougo" ? [id, "payg"] : [id];
  const row = planCatalog().find((p) => ids.includes(p.id) || ids.includes(p.canonicalId));
  return row ? row.monthlyFee : 0;
};

// ---------------------------------------------------------------------------------------------------- sign-up

/**
 * At sign-up submit: the `marketing` field for POST /users/register, and (with consent) the event id base kept for
 * the success page. Returns undefined without consent, so the request carries nothing about Meta.
 */
export const startSignupTracking = (plan: string): MarketingConsentPayload | undefined => {
  if (!hasAnalyticsConsent()) return undefined;
  const eventBase = newId();
  try {
    window.localStorage.setItem(PENDING_KEY, JSON.stringify({ eventBase, plan, ts: Date.now() }));
  } catch {
    /* the browser events are then skipped; the server's still go */
  }
  return marketingPayload({ eventBase });
};

/** Back from Stripe Checkout (`/register?callbackResponse=success`): the sign-up's browser events, once. */
export const completeSignupTracking = (): void => {
  if (typeof window === "undefined") return;
  let pending: { eventBase?: string; plan?: string; ts?: number } | null = null;
  try {
    pending = JSON.parse(window.localStorage.getItem(PENDING_KEY) || "null");
    window.localStorage.removeItem(PENDING_KEY);
  } catch {
    pending = null;
  }
  if (!pending || typeof pending.eventBase !== "string") return;
  if (!pending.ts || Date.now() - pending.ts > PENDING_MAX_AGE_MS) return;
  const base = pending.eventBase;
  trackStudioEvent("CompleteRegistration", { status: true }, `reg-${base}`);
  trackStudioEvent("WorkspaceCreated", {}, `ws-${base}`);
  const value = planMonthlyFee(pending.plan);
  if (value > 0) trackStudioEvent("Subscribe", { value, currency: CURRENCY }, `sub-${base}`);
};

// ------------------------------------------------------------------------------------------------- plan change

/** The `meta` field for POST billing/plan: the consent (false withdraws the one given at sign-up) and an event id. */
export const startPlanChangeTracking = (): MarketingConsentPayload => marketingPayload({ eventId: `sub-${newId()}` });

/** After a plan change succeeded: Subscribe for an upgrade to a paid plan, with the id the server uses too. */
export const completePlanChangeTracking = (meta: MarketingConsentPayload, plan: string, previousPlan?: string | null): void => {
  if (!meta.consent || !meta.eventId) return;
  const value = planMonthlyFee(plan);
  if (value > 0 && value > planMonthlyFee(previousPlan)) trackStudioEvent("Subscribe", { value, currency: CURRENCY }, meta.eventId);
};

// ------------------------------------------------------------------------------------------------ withdrawal

const WITHDRAWN_KEY = "ph_meta_withdrawn";

/**
 * Tell the server the signed-in user said no (POST /users/marketing-consent { consent: false }): their stored consent
 * and Meta cookie values go at once, so no Conversions API event is sent for them any more. Fire-and-forget; once
 * per browser session unless `force` (a fresh "no" in the banner). Only "no" is ever sent: a "yes" here never
 * re-enables server events (only a sign-up carrying consent does).
 */
export const syncConsentWithdrawal = ({ signedIn, force = false }: { signedIn: boolean; force?: boolean }): void => {
  if (typeof window === "undefined" || !signedIn) return;
  const c = getConsent();
  if (!c.decided || c.analytics) return;
  try {
    if (!force && window.sessionStorage.getItem(WITHDRAWN_KEY)) return;
    window.sessionStorage.setItem(WITHDRAWN_KEY, "1");
  } catch {
    if (!force) return;
  }
  photohawkApi
    .post("/users/marketing-consent", { service: "admin", body: { consent: false }, timeoutMs: 10_000 })
    .catch(() => {
      // Not recorded: try again on the next admin load.
      try {
        window.sessionStorage.removeItem(WITHDRAWN_KEY);
      } catch {
        /* ignore */
      }
    });
};

/** For tests: forget the one-time init. */
export const __resetStudioPixelForTests = (): void => {
  inited = false;
};
